import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '@pryme/ui';
import type { MeInfo, OrgDomain, Organization, Service } from './types';

const ORG_KEY = 'pryme.developers.org';

interface DevContextValue {
  environment: string;
  me: MeInfo | null;
  orgs: Organization[];
  org: Organization | null;
  selectOrg(id: string): void;
  reloadOrgs(): Promise<Organization[]>;
  services: Service[];
  servicesLoading: boolean;
  servicesError: unknown;
  reloadServices(): Promise<void>;
  /** The organization's domains. Production only: sandbox has no domains, so this stays empty. */
  orgDomains: OrgDomain[];
  reloadOrgDomains(): Promise<void>;
  logout(): Promise<void>;
}

const Ctx = createContext<DevContextValue | null>(null);

export function useDev() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDev outside DevProvider');
  return v;
}

export function DevProvider(props: { environment: string; me: MeInfo | null; onLogout(): void; children: ReactNode }) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<string | null>(() => localStorage.getItem(ORG_KEY));
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<unknown>(null);

  const reloadOrgs = useCallback(async () => {
    const r = await api<{ organizations: Organization[] }>('/v1/orgs');
    setOrgs(r.organizations);
    return r.organizations;
  }, []);

  useEffect(() => {
    void reloadOrgs().catch(() => undefined);
  }, [reloadOrgs]);

  const org = useMemo(() => orgs.find((o) => o.id === orgId) ?? orgs[0] ?? null, [orgs, orgId]);

  const selectOrg = useCallback((id: string) => {
    localStorage.setItem(ORG_KEY, id);
    setOrgId(id);
  }, []);

  const reloadServices = useCallback(async () => {
    if (!org) {
      setServices([]);
      return;
    }
    setServicesLoading(true);
    try {
      const r = await api<{ services: Service[] }>(`/v1/services?organizationId=${encodeURIComponent(org.id)}`);
      setServices(r.services);
      setServicesError(null);
    } catch (e) {
      setServicesError(e);
    } finally {
      setServicesLoading(false);
    }
  }, [org]);

  useEffect(() => {
    void reloadServices();
  }, [reloadServices]);

  const [orgDomains, setOrgDomains] = useState<OrgDomain[]>([]);
  const production = props.environment === 'production';
  const reloadOrgDomains = useCallback(async () => {
    if (!org || !production) return setOrgDomains([]);
    const r = await api<{ domains: OrgDomain[] }>(`/v1/orgs/${encodeURIComponent(org.id)}/domains`).catch(() => ({ domains: [] }));
    setOrgDomains(r.domains);
  }, [org, production]);

  useEffect(() => {
    void reloadOrgDomains();
  }, [reloadOrgDomains]);

  const logout = useCallback(async () => {
    await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    props.onLogout();
  }, [props]);

  const value: DevContextValue = {
    environment: props.environment, me: props.me, orgs, org, selectOrg, reloadOrgs,
    services, servicesLoading, servicesError, reloadServices, orgDomains, reloadOrgDomains, logout,
  };
  return <Ctx.Provider value={value}>{props.children}</Ctx.Provider>;
}
